import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarCheck, Plus, MapPin, User, Link2, Phone, Clock, FileText, X } from "lucide-react";
import { Link } from "react-router-dom";

const SYNC_STYLES = {
  synced: ["text-emerald-400", "Synced to Google Calendar"],
  failed: ["text-red-400", "Google Calendar sync failed"],
  syncing: ["text-slate-400", "Syncing to Google Calendar…"],
  not_connected: ["text-slate-400", "Not synced — Google Calendar not connected"],
};

export const GcalSyncBadge = ({ est, detailed = false }) => {
  const key = est.google_event_id ? "synced" : (est.gcal_sync_status || "unsynced");
  const [cls, label] = SYNC_STYLES[key] || ["text-amber-400", "Not synced to Google Calendar"];
  return (
    <div className={`text-xs font-mono mt-2 ${cls}`} data-testid={`gcal-sync-${est.id}`} data-sync-status={key}>
      <span className="flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> {label}</span>
      {detailed && key === "failed" && est.gcal_error && <p className="text-slate-400 mt-1" data-testid="gcal-sync-error">{est.gcal_error}</p>}
    </div>
  );
};

function toDateInput(d) {
  const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const dd = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${dd}`;
}
function fmtTime(iso, tz) {
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }); } catch { return iso; }
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [estimates, setEstimates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lead_id: "", slot: null, estimator_name: "", notes: "" });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [tz, setTz] = useState("America/Chicago");
  const [detail, setDetail] = useState(null); // selected estimate for details dialog

  const load = () => Promise.all([
    api.get("/estimates").then(r => setEstimates(r.data)),
    api.get("/leads").then(r => setLeads(r.data)),
    api.get("/org/settings").then(r => setTz(r.data.scheduling?.timezone || "UTC")),
    // Estimator options come from this org's team members that the owner/admin
    // has flagged as Bookable (via Team & Access). Legacy members with no
    // `bookable` field default to bookable=true on the backend.
    api.get("/org/members", { params: { bookable_only: true } }).then(r => {
      setMembers(r.data);
      setForm(f => f.estimator_name ? f : { ...f, estimator_name: r.data[0]?.full_name || "" });
    }),
  ]);
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open || !date) return;
    setSlotsLoading(true); setSlots([]); setForm(f => ({ ...f, slot: null }));
    api.get("/scheduling/availability", { params: { date: toDateInput(date) } })
      .then(r => { setSlots(r.data.slots || []); setTz(r.data.timezone || tz); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [open, date]);

  const sameDay = (a, b) => {
    const da = new Date(a), db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
  };
  const forDay = useMemo(() => estimates.filter(e => date && sameDay(e.scheduled_at, date)), [estimates, date]);
  const daysWith = estimates.map(e => new Date(e.scheduled_at));

  const book = async () => {
    if (!form.lead_id) return toast.error("Pick a lead");
    if (!form.slot) return toast.error("Pick an available time slot");
    if (!form.estimator_name) return toast.error("No team members available — invite one from Team & Access");
    try {
      await api.post("/estimates", {
        lead_id: form.lead_id, scheduled_at: form.slot.start,
        estimator_name: form.estimator_name, notes: form.notes,
      });
      toast.success("Inspection booked");
      setOpen(false);
      setForm({ lead_id: "", slot: null, estimator_name: members[0]?.full_name || "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
  };

  const [retrying, setRetrying] = useState(false);
  const retrySync = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/estimates/${detail.id}/gcal-retry`);
      setDetail({ ...detail, ...data });
      data.google_event_id ? toast.success("Synced to Google Calendar") : toast.error(data.gcal_error || "Google Calendar sync failed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google Calendar sync failed");
    } finally {
      setRetrying(false);
    }
  };

  const copyBookingLink = (leadId) => {
    const url = `${window.location.origin}/book/${leadId}`;
    navigator.clipboard?.writeText(url);
    toast.success("Homeowner booking link copied");
  };

  const unbooked = leads.filter(l => !["booked", "won"].includes(l.status));

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
      <header className="mb-6 flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Schedule</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Estimate Calendar</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">Times shown in {tz}</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold" data-testid="calendar-book-btn">
          <Plus className="w-4 h-4 mr-1" /> Book Inspection
        </Button>
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800 p-4 lg:col-span-1">
          <Calendar
            mode="single" selected={date} onSelect={setDate}
            modifiers={{ booked: daysWith }}
            modifiersClassNames={{ booked: "bg-orange-500/20 text-orange-300 font-bold ring-1 ring-orange-500/50" }}
            className="rounded-md" data-testid="estimate-calendar"
          />
          <div className="mt-4 flex items-center gap-3 text-xs text-slate-400 font-mono">
            <span className="inline-block w-3 h-3 rounded-sm bg-orange-500/40 ring-1 ring-orange-500/60" /> Booked date
          </div>
        </Card>

        <Card className="lg:col-span-2 bg-slate-900 border-slate-800 p-5">
          <h2 className="font-display text-xl font-semibold mb-1">
            {date?.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </h2>
          <p className="text-sm text-slate-500 mb-4">{forDay.length} inspection{forDay.length !== 1 ? "s" : ""} scheduled</p>
          <div className="space-y-3">
            {forDay.length === 0 && (
              <div className="text-center py-12 text-slate-500 text-sm">No inspections scheduled for this day.</div>
            )}
            {forDay.map((e) => {
              const relatedLead = leads.find(l => l.id === e.lead_id);
              return (
              <button type="button" key={e.id} onClick={() => setDetail({ ...e, lead: relatedLead })}
                data-testid={`estimate-${e.id}`}
                className="w-full text-left p-4 rounded-lg bg-slate-800/40 border border-slate-800 hover:border-orange-500/40 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CalendarCheck className={`w-4 h-4 ${e.google_event_id ? "text-emerald-400" : e.gcal_sync_status === "failed" ? "text-red-400" : "text-slate-500"}`} />
                    <span className="font-mono text-sm font-bold text-orange-400">{fmtTime(e.scheduled_at, tz)}</span>
                  </div>
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 capitalize">{e.status}</Badge>
                </div>
                <p className="font-semibold">{e.caller_name}</p>
                <div className="mt-1 flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.address}</span>
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {e.estimator_name}</span>
                </div>
                {e.notes && <p className="text-xs text-slate-500 mt-2 italic">{e.notes}</p>}
                <GcalSyncBadge est={e} />
              </button>
              );
            })}
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl">
          <DialogHeader><DialogTitle>Book roof inspection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Lead</Label>
              <Select value={form.lead_id} onValueChange={(v) => setForm({ ...form, lead_id: v })}>
                <SelectTrigger data-testid="book-lead-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Choose a lead" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {unbooked.map(l => <SelectItem key={l.id} value={l.id}>{l.caller_name} — {l.issue}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.lead_id && (
                <button type="button" onClick={() => copyBookingLink(form.lead_id)}
                  data-testid="copy-booking-link"
                  className="mt-2 text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> Copy homeowner booking link
                </button>
              )}
            </div>
            <div>
              <Label>Date</Label>
              <Input value={date?.toLocaleDateString()} readOnly className="bg-slate-800 border-slate-700" />
              <p className="text-xs text-slate-500 mt-1">Change the day by clicking the calendar on the left.</p>
            </div>
            <div>
              <Label className="flex items-center justify-between">Available times <span className="text-xs text-slate-500 font-mono">{tz}</span></Label>
              {slotsLoading && <p className="text-slate-500 text-sm py-3" data-testid="book-slots-loading">Loading availability…</p>}
              {!slotsLoading && slots.length === 0 && (
                <p className="text-slate-500 text-sm py-3" data-testid="book-no-slots">No slots on this day — pick another date on the calendar.</p>
              )}
              {!slotsLoading && slots.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto" data-testid="book-slot-grid">
                  {slots.map((s) => {
                    const active = form.slot?.start === s.start;
                    return (
                      <button key={s.start} type="button" disabled={!s.available} onClick={() => setForm({ ...form, slot: s })}
                        data-testid={`book-slot-${s.start}`}
                        className={`p-2 rounded-md text-xs font-mono border transition-colors ${
                          !s.available
                            ? "bg-slate-800/30 border-slate-800 text-slate-600 line-through cursor-not-allowed"
                            : active
                            ? "bg-orange-500 border-orange-400 text-slate-900 font-bold"
                            : "bg-slate-800 border-slate-700 text-slate-200 hover:border-orange-500/60"
                        }`}>
                        {fmtTime(s.start, tz)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label>Estimator</Label>
              {members.length === 0 ? (
                <p className="text-sm text-slate-400 bg-slate-800/50 border border-slate-700 rounded-md p-3" data-testid="book-no-estimators">
                  No active team members yet.{" "}
                  <Link to="/app/team" className="text-orange-400 hover:text-orange-300 underline">
                    Invite one from Team & Access
                  </Link>{" "}
                  to assign appointments.
                </p>
              ) : (
                <Select value={form.estimator_name} onValueChange={(v) => setForm({ ...form, estimator_name: v })}>
                  <SelectTrigger data-testid="book-estimator" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {members.map(m => (
                      <SelectItem key={m.user_id} value={m.full_name} data-testid={`book-estimator-opt-${m.user_id}`}>
                        {m.full_name} <span className="text-slate-500 text-xs">· {m.role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div><Label>Notes</Label><Input data-testid="book-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
          </div>
          <DialogFooter>
            <Button onClick={book} data-testid="book-submit"
              disabled={!form.lead_id || !form.slot || !form.estimator_name}
              className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold disabled:opacity-50">
              Book inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="estimate-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-emerald-400" /> Inspection details
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Homeowner</p>
                <p className="font-semibold text-lg">{detail.caller_name}</p>
                {detail.lead?.caller_phone && (
                  <a href={`tel:${detail.lead.caller_phone}`} className="text-xs text-orange-400 hover:text-orange-300 font-mono inline-flex items-center gap-1 mt-1" data-testid="estimate-detail-call">
                    <Phone className="w-3 h-3" /> {detail.lead.caller_phone}
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">When</p>
                  <p className="text-sm flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" /> {new Date(detail.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz })}</p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{tz}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Estimator</p>
                  <p className="text-sm flex items-center gap-1"><User className="w-3 h-3 text-slate-400" /> {detail.estimator_name}</p>
                </div>
              </div>
              {detail.address && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Address</p>
                  <p className="text-sm flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {detail.address}</p>
                </div>
              )}
              {detail.lead?.issue && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Issue</p>
                  <Badge className="bg-orange-500/15 text-orange-300 border border-orange-500/30">{detail.lead.issue}</Badge>
                </div>
              )}
              {detail.notes && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes</p>
                  <p className="text-sm text-slate-300 flex items-start gap-1"><FileText className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" /> {detail.notes}</p>
                </div>
              )}
              <div data-testid="estimate-detail-gcal">
                <GcalSyncBadge est={detail} detailed />
                {!detail.google_event_id && detail.gcal_sync_status === "not_connected" && (
                  <Link to="/app/settings" className="text-xs text-orange-400 hover:text-orange-300 mt-2 inline-block" data-testid="estimate-gcal-settings-link">
                    Connect Google Calendar in Settings
                  </Link>
                )}
                {!detail.google_event_id && detail.gcal_sync_status !== "not_connected" && (
                  <Button size="sm" onClick={retrySync} disabled={retrying} data-testid="estimate-gcal-retry-btn"
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold">
                    {retrying ? "Syncing…" : "Retry Google Calendar sync"}
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {detail?.lead_id && (
              <Button variant="ghost" onClick={() => { navigate(`/app/chat/${detail.lead_id}`); setDetail(null); }}
                data-testid="estimate-detail-chat" className="text-orange-400 hover:text-orange-300 hover:bg-slate-800">
                Open conversation
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDetail(null)} data-testid="estimate-detail-close" className="text-slate-300 hover:bg-slate-800">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
